import { Component, ReactNode, ErrorInfo } from 'react';
import { isChunkLoadError, reloadOnceForChunkError } from '@/lib/chunkReload';
import { reportClientError } from '@/lib/clientErrorReport';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Backstop for the deploy/chunk-load case: if a failed dynamic import
    // reached this boundary (e.g. not caught by lazyWithReload or the
    // vite:preloadError handler), reload ONCE to pull the fresh index.html.
    // reloadOnceForChunkError() is guarded so a permanently-missing chunk
    // reloads at most once and then falls through to the error UI below.
    if (isChunkLoadError(error)) {
      reloadOnceForChunkError();
    }
    // Local, client-side only (browser console) — Sentry removed, so nothing
    // egresses to a third party. Keep it PII-light (message + React tree).
    console.error('[ErrorBoundary]', error.message, info.componentStack);
    // Run Q / Q-2 — and to OUR server, as five fixed fields under the PHI rule
    // (route, top frame, class, message capped at 200, bundle). Without this a
    // render crash leaves no trace an operator can read: the Front Desk
    // Confirmations tab threw on every click for eight weeks and the only
    // evidence in the logs was the absence of a request.
    reportClientError(error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex items-center justify-center bg-background py-16 px-6 min-h-[60vh]">
          <div className="text-center space-y-4 p-8 max-w-md">
            <div className="text-4xl" aria-hidden="true">⚠️</div>
            <h1 className="text-2xl font-bold text-foreground">
              Something went wrong loading this page
            </h1>
            <p className="text-muted-foreground">
              An unexpected error occurred. Refreshing the page usually fixes it.
              If it keeps happening, please let us know.
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <pre className="text-left text-xs bg-muted p-4 rounded overflow-auto">
                {this.state.error.message}
              </pre>
            )}
            <div className="flex flex-col sm:flex-row gap-2 justify-center items-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                Refresh page
              </button>
              <a
                href="mailto:support@mskinsight.com?subject=Settings%20page%20error"
                className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Report to support
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
